import {
  CsmAvailability as CsmAvailabilityEntry,
  useGetMontageAvailabilityQuery,
} from "@/services/montageMeetings";
import { fitsInRanges, toMinutes } from "@/helpers/availability";
import {
  buildMeetingSlots,
  busySlotFor,
  isSelectableSlot,
} from "@/helpers/meetingSlots";
import { FC, useEffect } from "react";
import AvailabilitySlotGrid from "./AvailabilitySlotGrid";

const UNAVAILABLE = {
  unset: "Non renseigné",
  off: "Congé",
  holiday: "Férié",
} as const;

/** Motif d'indisponibilité du créneau d'une heure, ou `null`. Sans `ranges` (ancienne API), rien. */
export const unavailableReasonOf = (
  entry: CsmAvailabilityEntry | undefined,
  hour: string,
  durationMinutes = entry?.slotMinutes ?? 60,
): string | null => {
  if (!entry || entry.ranges === undefined) return null;
  if (entry.unavailableReason) return UNAVAILABLE[entry.unavailableReason];
  const start = toMinutes(hour);
  return fitsInRanges(entry.ranges, start, start + durationMinutes)
    ? null
    : "Hors horaires";
};

interface IProps {
  durationMinutes?: number;
  excludeMeetingId?: string;
  onInvalidSelection?: () => void;
  /** Jour observé, au format yyyy-MM-dd. */
  date: string;
  csmId: string | null;
  /** Fourni quand on choisit un créneau : seuls les créneaux libres deviennent
   *  cliquables. Absent, le composant reste en lecture seule. */
  onSelect?: (hour: string) => void;
  selectedHour?: string | null;
  /** ADMIN et CSM surchargent un créneau déjà pris ; un commercial non. La
   *  disponibilité les informe, elle ne les contraint pas. */
  allowBusy?: boolean;
  /** ADMIN et CSM posent hors disponibilité (avertis) ; les autres ne le peuvent pas. */
  allowUnavailable?: boolean;
}

/**
 * Disponibilité d'un CSM sur une journée, par créneau d'une heure. Lecture
 * seule : ce composant informe, il ne choisit pas.
 */
const CsmAvailability: FC<IProps> = ({
  date,
  csmId,
  onSelect,
  selectedHour = null,
  allowBusy = false,
  allowUnavailable = false,
  durationMinutes,
  excludeMeetingId,
  onInvalidSelection,
}) => {
  const { currentData: data, isFetching } = useGetMontageAvailabilityQuery({
    date,
  });

  const entry = data?.find((candidate) => candidate.csmId === csmId);
  const duration = durationMinutes ?? entry?.slotMinutes ?? 60;
  useEffect(() => {
    if (
      selectedHour &&
      !allowUnavailable &&
      !isSelectableSlot(entry, selectedHour, {
        durationMinutes: duration,
        excludeMeetingId,
        allowBusy,
      })
    )
      onInvalidSelection?.();
  }, [
    entry,
    selectedHour,
    duration,
    excludeMeetingId,
    allowBusy,
    allowUnavailable,
    onInvalidSelection,
  ]);

  if (!csmId) {
    return (
      <p className="fs--1 text-600 mb-0">
        Sélectionnez un CSM pour voir ses disponibilités.
      </p>
    );
  }

  if (isFetching && !data) {
    return <p className="fs--1 text-600 mb-0">Chargement des créneaux…</p>;
  }

  const unavailableOf = (hour: string) =>
    unavailableReasonOf(entry, hour, duration);
  const slots = entry?.unavailableReason
    ? []
    : buildMeetingSlots(
        entry?.ranges ?? [],
        entry?.slotMinutes ?? 60,
        duration,
      );

  return (
    <>
      {/* Grille plutôt que douze lignes pleine largeur : sur une modale large, une
        ligne par heure occupait la moitié de l'écran pour très peu d'information. */}
      {!slots.length && (
        <p role="status" className="fs--1 text-600 mb-0">
          {entry?.unavailableReason
            ? UNAVAILABLE[entry.unavailableReason]
            : "Aucun créneau disponible pour cette durée."}
        </p>
      )}
      <AvailabilitySlotGrid
        items={slots.map(({ start, end }) => {
          const busy = busySlotFor(entry, start, duration, excludeMeetingId);
          const off = unavailableOf(start);
          return {
            start,
            end,
            state: busy ? "busy" : off ? "unavailable" : "free",
            selectable:
              !!onSelect &&
              !isFetching &&
              (!busy || allowBusy) &&
              (!off || allowUnavailable),
            detail: busy ? busy.companyName : off,
          };
        })}
        selectedHour={selectedHour}
        onSelect={onSelect}
      />
      {selectedHour && unavailableOf(selectedHour) && (
        <p role="status" className="fs--2 text-warning mt-1 mb-0">
          Créneau retenu hors des disponibilités ({unavailableOf(selectedHour)}
          ).
        </p>
      )}
    </>
  );
};

export default CsmAvailability;
